import React, { useState, useEffect } from 'react'
import { Box, BoxProps, Image as ChakraImage } from '@chakra-ui/react'
import { useInView } from 'react-intersection-observer'

import { SKELETON_IMAGE_BASE64 } from './SkinCardImageV2'

type ImageLoadProps = {
  defaultSrc?: string
  imageSrc: string | undefined
  fallback?: string
  distance?: number
  width?: string | number
  height?: string | number
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  alt?: string
  className?: string
  style?: React.CSSProperties
  initIsLoaded?: boolean
  loading?: 'lazy' | 'eager'
}

const ImageLoad = ({
  defaultSrc = SKELETON_IMAGE_BASE64,
  imageSrc,
  fallback,
  distance = 500,
  initIsLoaded = false,
  loading = 'lazy',
  ...rest
}: ImageLoadProps & BoxProps) => {
  const [currentSrc, setCurrentSrc] = useState(defaultSrc)
  const [hasError, setHasError] = useState(false)
  const [isLoaded, setIsLoaded] = useState(initIsLoaded)

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: `${distance}px`,
    triggerOnce: true,
  })

  useEffect(() => {
    if (!imageSrc) {
      setCurrentSrc(fallback ?? defaultSrc)
      return
    }
    if (loading === 'lazy') {
      if (inView) {
        setCurrentSrc(imageSrc)
      } else {
        setCurrentSrc(defaultSrc)
      }
    } else {
      setCurrentSrc(imageSrc)
    }
  }, [imageSrc, inView, defaultSrc, fallback, loading])

  const handleError = () => {
    setHasError(true)
    if (fallback) {
      setCurrentSrc(fallback)
    } else {
      setCurrentSrc(defaultSrc)
    }
  }
  return (
    <Box ref={ref} position="relative" overflow="hidden" {...rest}>
      {!isLoaded && !hasError && (
        <ChakraImage
          src={defaultSrc}
          width="100%"
          objectFit="cover"
          position="absolute"
          top={0}
          left={0}
        />
      )}
      <ChakraImage
        src={currentSrc}
        alt={rest?.alt}
        width="100%"
        objectFit="cover"
        opacity={isLoaded ? 1 : 0.8}
        transition="opacity 0.3s ease"
        onError={handleError}
        loading={loading}
        onLoad={() => {
          setIsLoaded(true)
        }}
        fetchPriority={loading === 'eager' ? 'high' : 'auto'}
      />
    </Box>
  )
}

export default ImageLoad
